import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  addKlachtAction,
  fetchWasserijItemsForVervolg,
  KlachtError,
  type VervolgExtraInput,
  type WasserijItemForPicker,
} from '../../../lib/klachten';
import { fetchWasserijProducts, type WasserijProduct } from '../../../lib/wasserij-items';
import { formatCurrency } from '../../../lib/format';
import { useKlachtDialogStore } from '../../../stores/klacht-dialog-store';

interface Props {
  onAdded?: () => void;
}

type ItemMode = 'skip' | 'free' | 'paid';

/** Een door de medewerker toegevoegde behandeling op een meegenomen kleed. */
interface ExtraEntry {
  productId: string;
  name: string;
  bill: 'free' | 'paid';
}

function describeAfmeting(item: WasserijItemForPicker): string {
  if (item.diameterCm) return `Ø ${item.diameterCm} cm`;
  if (item.lengteCm && item.breedteCm) {
    return `${item.lengteCm} × ${item.breedteCm} cm`;
  }
  if (item.oppervlakteM2) return `${item.oppervlakteM2} m²`;
  return 'afmetingen onbekend';
}

const MODE_OPTIONS: { value: ItemMode; label: string }[] = [
  { value: 'skip', label: 'Niet meenemen' },
  { value: 'free', label: 'Gratis' },
  { value: 'paid', label: 'Factureren' },
];

/**
 * Action-dialog: vervolg-werkorder. Per item kies je: niet meenemen, gratis
 * meenemen, of factureren tegen catalogprijs. De Apex
 * `NewWerkOrderActionHandler` doet de daadwerkelijke insert + clone.
 */
export function AddNewWerkOrderDialog({ onAdded }: Props) {
  const openDialog = useKlachtDialogStore((s) => s.openDialog);
  const payload = useKlachtDialogStore((s) => s.payload);
  const close = useKlachtDialogStore((s) => s.close);

  const isOpen = openDialog === 'addNewWo';
  const klachtId = payload.klachtId;
  const werkOrderId = payload.werkOrderId;

  const [items, setItems] = useState<WasserijItemForPicker[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [itemModes, setItemModes] = useState<Record<string, ItemMode>>({});
  // Extra behandelingen per kleed (sourceItemId → entries).
  const [extrasByItem, setExtrasByItem] = useState<Record<string, ExtraEntry[]>>({});
  // Catalog-cache per hoofdproduct-Id (primaryProductId → behandelingen).
  const [productCache, setProductCache] = useState<Record<string, WasserijProduct[]>>({});
  const [openPickerItemId, setOpenPickerItemId] = useState<string | null>(null);
  const [loadingProductsFor, setLoadingProductsFor] = useState<string | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [bevestigingenSturen, setBevestigingenSturen] = useState(false);
  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setItemModes({});
    setExtrasByItem({});
    setProductCache({});
    setOpenPickerItemId(null);
    setLoadingProductsFor(null);
    setProductsError(null);
    setBevestigingenSturen(false);
    setReden('');
    setSubmitting(false);
    setError(null);
    if (!werkOrderId) {
      setLoadError('Geen werkorder-id meegegeven');
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoadingItems(true);
    setLoadError(null);
    fetchWasserijItemsForVervolg(werkOrderId)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
        // Default: alle items op 'free' — matcht gedrag van legacy kostenloos=true.
        const defaults: Record<string, ItemMode> = {};
        for (const r of rows) defaults[r.id] = 'free';
        setItemModes(defaults);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Items laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoadingItems(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, werkOrderId]);

  const setMode = useCallback((id: string, mode: ItemMode) => {
    setItemModes((prev) => ({ ...prev, [id]: mode }));
    // Kleed niet meenemen → behandelingen vervallen (consistent met handler-guard).
    if (mode === 'skip') {
      setExtrasByItem((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setOpenPickerItemId((cur) => (cur === id ? null : cur));
    }
  }, []);

  const openPicker = useCallback(
    (item: WasserijItemForPicker) => {
      setOpenPickerItemId(item.id);
      setProductsError(null);
      const pid = item.primaryProductId;
      if (!pid || productCache[pid]) return; // geen hoofdproduct of al gecachet
      setLoadingProductsFor(item.id);
      fetchWasserijProducts('extra', pid)
        .then((list) => {
          setProductCache((prev) => ({ ...prev, [pid]: list }));
        })
        .catch((err) => {
          setProductsError(err instanceof Error ? err.message : 'Behandelingen laden mislukt');
        })
        .finally(() => {
          setLoadingProductsFor((cur) => (cur === item.id ? null : cur));
        });
    },
    [productCache]
  );

  const addExtra = useCallback((sourceItemId: string, product: WasserijProduct) => {
    setExtrasByItem((prev) => {
      const list = prev[sourceItemId] ?? [];
      if (list.some((e) => e.productId === product.id)) return prev; // geen dubbele
      return {
        ...prev,
        [sourceItemId]: [...list, { productId: product.id, name: product.name, bill: 'paid' }],
      };
    });
    setOpenPickerItemId(null);
  }, []);

  const removeExtra = useCallback((sourceItemId: string, productId: string) => {
    setExtrasByItem((prev) => {
      const list = prev[sourceItemId] ?? [];
      const next = list.filter((e) => e.productId !== productId);
      const updated = { ...prev };
      if (next.length === 0) delete updated[sourceItemId];
      else updated[sourceItemId] = next;
      return updated;
    });
  }, []);

  const setExtraBill = useCallback(
    (sourceItemId: string, productId: string, bill: 'free' | 'paid') => {
      setExtrasByItem((prev) => {
        const list = prev[sourceItemId] ?? [];
        return {
          ...prev,
          [sourceItemId]: list.map((e) => (e.productId === productId ? { ...e, bill } : e)),
        };
      });
    },
    []
  );

  const { meegenomenCount, factureerbaarCount, subtotaal, extraCount, extraFactureerbaarCount } =
    useMemo(() => {
      let mee = 0;
      let fact = 0;
      let sub = 0;
      let exc = 0;
      let exFact = 0;
      for (const item of items) {
        const mode = itemModes[item.id] ?? 'skip';
        if (mode === 'skip') continue;
        mee++;
        if (mode === 'paid') {
          fact++;
          sub += item.verkoopprijsInclBtw ?? 0;
        }
        for (const ex of extrasByItem[item.id] ?? []) {
          exc++;
          if (ex.bill === 'paid') exFact++;
        }
      }
      return {
        meegenomenCount: mee,
        factureerbaarCount: fact,
        subtotaal: sub,
        extraCount: exc,
        extraFactureerbaarCount: exFact,
      };
    }, [items, itemModes, extrasByItem]);

  const canSubmit = meegenomenCount > 0 && reden.trim().length > 0 && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!klachtId) {
      setError('Geen klacht-id meegegeven');
      return;
    }
    if (meegenomenCount === 0) {
      setError('Selecteer minstens één item om mee te nemen');
      return;
    }
    if (!reden.trim()) {
      setError('Reden voor vervolg is verplicht');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const wasserijItemIds: string[] = [];
      const factureerbareItemIds: string[] = [];
      const extras: VervolgExtraInput[] = [];
      for (const item of items) {
        const mode = itemModes[item.id] ?? 'skip';
        if (mode === 'skip') continue;
        wasserijItemIds.push(item.id);
        if (mode === 'paid') factureerbareItemIds.push(item.id);
        for (const ex of extrasByItem[item.id] ?? []) {
          extras.push({ sourceItemId: item.id, productId: ex.productId, bill: ex.bill });
        }
      }
      await addKlachtAction({
        klachtId,
        actionType: 'NewWerkOrder',
        params: {
          wasserijItemIds,
          factureerbareItemIds,
          bevestigingenSturen,
          redenVoorVervolg: reden.trim(),
          ...(extras.length > 0 ? { extras } : {}),
        },
      });
      close();
      onAdded?.();
    } catch (err) {
      setError(err instanceof KlachtError ? err.message : 'Vervolg-WO aanmaken mislukt');
    } finally {
      setSubmitting(false);
    }
  }, [
    klachtId,
    items,
    itemModes,
    extrasByItem,
    meegenomenCount,
    bevestigingenSturen,
    reden,
    close,
    onAdded,
  ]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Vervolg-werkorder</h2>
          <p className="text-xs text-slate-500 mt-1">
            Kies per item wat je ermee doet: niet meenemen, gratis opnieuw uitvoeren,
            of factureren tegen catalogprijs.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          <fieldset>
            <legend className="text-sm text-slate-700 mb-1">
              Items <span className="text-red-600">*</span>
            </legend>

            {loadingItems && <p className="text-xs text-slate-500">Items laden…</p>}
            {loadError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {loadError}
              </p>
            )}
            {!loadingItems && !loadError && items.length === 0 && (
              <p className="text-xs text-slate-500">Geen items gevonden op deze werkorder.</p>
            )}

            <div className="space-y-1.5">
              {items.map((item) => {
                const mode = itemModes[item.id] ?? 'skip';
                const itemExtras = extrasByItem[item.id] ?? [];
                const pickerOpen = openPickerItemId === item.id;
                const catalog = item.primaryProductId
                  ? productCache[item.primaryProductId]
                  : undefined;
                return (
                  <div
                    key={item.id}
                    className={clsx(
                      'rounded border',
                      mode === 'skip' ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200'
                    )}
                  >
                    <div
                      className={clsx(
                        'flex items-center gap-3 px-2 py-1.5',
                        mode === 'skip' && 'opacity-60'
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{item.naam}</div>
                        <div className="text-xs text-slate-500">
                          {describeAfmeting(item)}
                          {item.verkoopprijsInclBtw != null && (
                            <span className="ml-2">
                              · {formatCurrency(item.verkoopprijsInclBtw)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        role="radiogroup"
                        aria-label={`Mode voor ${item.naam}`}
                        className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs"
                      >
                        {MODE_OPTIONS.map((opt, idx) => {
                          const active = mode === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              disabled={submitting}
                              onClick={() => setMode(item.id, opt.value)}
                              className={clsx(
                                'px-2.5 py-1 transition-colors',
                                idx > 0 && 'border-l border-slate-300',
                                active
                                  ? opt.value === 'paid'
                                    ? 'bg-indigo-600 text-white'
                                    : opt.value === 'free'
                                      ? 'bg-emerald-600 text-white'
                                      : 'bg-slate-500 text-white'
                                  : 'bg-white text-slate-700 hover:bg-slate-50'
                              )}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Extra behandelingen — alleen voor meegenomen kleden */}
                    {mode !== 'skip' && (
                      <div className="px-2 pb-2 pt-1 border-t border-slate-100 space-y-1.5">
                        {itemExtras.length > 0 && (
                          <ul className="space-y-1">
                            {itemExtras.map((ex) => (
                              <li
                                key={ex.productId}
                                className="flex items-center gap-2 text-xs"
                              >
                                <span className="flex-1 min-w-0 truncate text-slate-700">
                                  {ex.name}
                                </span>
                                <div
                                  role="radiogroup"
                                  aria-label={`Facturatie voor ${ex.name}`}
                                  className="inline-flex rounded border border-slate-300 overflow-hidden"
                                >
                                  {(['free', 'paid'] as const).map((b, idx) => {
                                    const active = ex.bill === b;
                                    return (
                                      <button
                                        key={b}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        disabled={submitting}
                                        onClick={() => setExtraBill(item.id, ex.productId, b)}
                                        className={clsx(
                                          'px-2 py-0.5 transition-colors',
                                          idx > 0 && 'border-l border-slate-300',
                                          active
                                            ? b === 'paid'
                                              ? 'bg-indigo-600 text-white'
                                              : 'bg-emerald-600 text-white'
                                            : 'bg-white text-slate-700 hover:bg-slate-50'
                                        )}
                                      >
                                        {b === 'paid' ? 'Factureren' : 'Gratis'}
                                      </button>
                                    );
                                  })}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeExtra(item.id, ex.productId)}
                                  disabled={submitting}
                                  title="Behandeling verwijderen"
                                  className="text-slate-400 hover:text-red-600 leading-none disabled:opacity-50"
                                >
                                  ✕
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        {!item.primaryProductId ? (
                          <p className="text-xs text-slate-400">
                            Geen hoofdproduct — behandeling toevoegen niet mogelijk.
                          </p>
                        ) : pickerOpen ? (
                          <div className="rounded border border-slate-200 bg-slate-50/60 p-2 space-y-1">
                            {loadingProductsFor === item.id && (
                              <p className="text-xs text-slate-500">Behandelingen laden…</p>
                            )}
                            {productsError && (
                              <p className="text-xs text-red-600">{productsError}</p>
                            )}
                            {catalog && catalog.length === 0 && (
                              <p className="text-xs text-slate-500">
                                Geen behandelingen beschikbaar voor dit hoofdproduct.
                              </p>
                            )}
                            {catalog && catalog.length > 0 && (
                              <div className="max-h-44 overflow-y-auto space-y-1">
                                {catalog.map((p) => {
                                  const alreadyAdded = itemExtras.some(
                                    (e) => e.productId === p.id
                                  );
                                  return (
                                    <button
                                      key={p.id}
                                      type="button"
                                      disabled={submitting || alreadyAdded}
                                      onClick={() => addExtra(item.id, p)}
                                      className="w-full text-left text-xs px-2 py-1.5 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                                    >
                                      <span className="truncate">{p.name}</span>
                                      {alreadyAdded && (
                                        <span className="text-[10px] text-slate-400">
                                          toegevoegd
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => setOpenPickerItemId(null)}
                                className="text-xs text-slate-600 hover:underline"
                              >
                                Sluiten
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openPicker(item)}
                            disabled={submitting}
                            className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                          >
                            + Behandeling toevoegen
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {items.length > 0 && (
              <div className="mt-2 flex items-center justify-between text-xs text-slate-600 px-1">
                <span>
                  {meegenomenCount} item{meegenomenCount === 1 ? '' : 's'} meegenomen
                  {factureerbaarCount > 0 && ` · waarvan ${factureerbaarCount} factureerbaar`}
                  {extraCount > 0 &&
                    ` · ${extraCount} behandeling${extraCount === 1 ? '' : 'en'}${
                      extraFactureerbaarCount > 0
                        ? ` (${extraFactureerbaarCount} factureerbaar)`
                        : ''
                    }`}
                </span>
                <span className="font-medium text-slate-900">
                  Te factureren: {formatCurrency(subtotaal)}
                  {extraFactureerbaarCount > 0 && (
                    <span className="font-normal text-slate-500"> + behandelingen (catalogprijs)</span>
                  )}
                </span>
              </div>
            )}
          </fieldset>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={bevestigingenSturen}
              onChange={(e) => setBevestigingenSturen(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700">
              Stuur ophaalmoment-bevestiging per e-mail
              <span className="block text-xs text-slate-500">
                Standaard uit voor klacht-vervolgen — neem zelf contact op met
                de klant. Aan = automatische mail zoals bij een normale order.
              </span>
            </span>
          </label>

          <label className="block">
            <span className="text-sm text-slate-700">
              Reden voor vervolg <span className="text-red-600">*</span>
            </span>
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={3}
              placeholder="Bv. herreiniging na klacht over vlek + ontgeuren los gefactureerd"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={submitting}
            />
          </label>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Bezig…' : `Vervolg-WO aanmaken (${meegenomenCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
